// "Originals by": when the person uploading expects to upload the originals, for "Upload previews now, originals
// later". Presets are end-of-day times in this browser's time zone — tonight (23:00 today, offered until 22:00),
// tomorrow, in 2 days, in 3 days — or a custom date and time (at least an hour and at most 60 days ahead; the
// server allows a minute so a staging test can use a short promise).
//
// Pure (the clock is a parameter), so it is unit-tested.

export type OriginalsByPreset = "tonight" | "tomorrow" | "in2" | "in3" | "custom";

export const PRESETS: Exclude<OriginalsByPreset, "custom">[] = ["tonight", "tomorrow", "in2", "in3"];
export const DEFAULT_PRESET: OriginalsByPreset = "tomorrow";
/** The hour the day presets end at (local time). */
export const END_OF_DAY_HOUR = 23;
/** "Tonight" is offered until this local hour. */
export const TONIGHT_UNTIL_HOUR = 22;
export const CUSTOM_MIN_AHEAD_MS = 60 * 60 * 1000;
export const MAX_AHEAD_MS = 60 * 24 * 60 * 60 * 1000;

const endOfDay = (now: number, plusDays: number): number => {
  const d = new Date(now);
  d.setDate(d.getDate() + plusDays);
  d.setHours(END_OF_DAY_HOUR, 0, 0, 0);
  return d.getTime();
};

/** The preset's time (epoch ms), or null when it doesn't apply now ("tonight" late in the evening). */
export const presetTime = (p: OriginalsByPreset, now: number = Date.now()): number | null => {
  switch (p) {
    case "tonight":
      return new Date(now).getHours() < TONIGHT_UNTIL_HOUR ? endOfDay(now, 0) : null;
    case "tomorrow":
      return endOfDay(now, 1);
    case "in2":
      return endOfDay(now, 2);
    case "in3":
      return endOfDay(now, 3);
    default:
      return null;
  }
};

/** The presets on offer now, with their times. */
export const availablePresets = (now: number = Date.now()) =>
  PRESETS.map((key) => ({ key, at: presetTime(key, now) })).filter((p): p is { key: Exclude<OriginalsByPreset, "custom">; at: number } => p.at != null);

/** A custom time: an hour to 60 days ahead. */
export const validateCustom = (ms: number | null | undefined, now: number = Date.now()): "ok" | "missing" | "soon" | "far" => {
  if (ms == null || !Number.isFinite(ms)) return "missing";
  if (ms < now + CUSTOM_MIN_AHEAD_MS) return "soon";
  if (ms > now + MAX_AHEAD_MS) return "far";
  return "ok";
};

const pad = (n: number) => String(n).padStart(2, "0");

/** epoch ms → a datetime-local input value ("2026-10-18T23:00") in local time. */
export const toLocalInputValue = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** A datetime-local input value → epoch ms (local time), or null. */
export const fromLocalInputValue = (v: string): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(v || "");
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), 0, 0);
  return Number.isFinite(d.getTime()) ? d.getTime() : null;
};

/** The preset matching a time exactly (e.g. an existing promise), else "custom". */
export const presetFor = (ms: number, now: number = Date.now()): OriginalsByPreset =>
  availablePresets(now).find((p) => p.at === ms)?.key ?? "custom";

const tagOf = (lang: string | undefined) => {
  const base = (lang || "en").toLowerCase();
  return base === "en" || base.startsWith("en-") ? "en-GB" : base;
};

/** "Sat 18 Oct, 23:00" in the app's language (this browser's time zone). */
export const formatOriginalsBy = (ms: number, lang?: string, timeZone?: string): string => {
  const opts: Intl.DateTimeFormatOptions = { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", ...(timeZone ? { timeZone } : {}) };
  try {
    return new Intl.DateTimeFormat(tagOf(lang), opts).format(new Date(ms));
  } catch {
    return new Intl.DateTimeFormat("en-GB", opts).format(new Date(ms));
  }
};
